import type { LocalizedDict } from '../index'

// Admin → Shipping → Ship From Locations (page, address book, per-store assignment).

export type ShipFromLocationsStrings = {
  page: {
    title: string
    description: string
    addressesTitle: string
    addressesHint: string
    storesTitle: string
    storesHint: string
  }
  addresses: {
    loading: string
    loadError: string
    default: string
    makeDefault: string
    edit: (address: string) => string
    delete: (address: string) => string
    add: string
    editTitle: string
    newTitle: string
    street: string
    suite: string
    city: string
    state: string
    zip: string
    phone: string
    defaultCheckbox: string
    saveFailed: string
    cancel: string
    saving: string
    save: string
  }
  stores: {
    senderNameFor: (shop: string) => string
    addressFor: (shop: string) => string
    defaultOption: (address: string) => string
    noAddress: string
    replace: string
    upload: string
    remove: string
    failed: string
    saved: string
    loading: string
    loadError: string
    searchPlaceholder: (count: number) => string
    searchLabel: string
    lastNotSaved: string
    colStore: string
    colShipFrom: string
    colSender: string
    colSlipLogo: string
  }
}

export const shipFromLocationsI18n: LocalizedDict<ShipFromLocationsStrings> = {
  en: {
    page: {
      title: 'Ship From Locations',
      description:
        "The addresses labels ship from, and which one each store uses. The sender name on the label is the store's — leave it empty to print the shop name. A label is not bought when no address applies.",
      addressesTitle: '1 · Addresses',
      addressesHint: 'Street, city, ZIP — no name',
      storesTitle: '2 · Stores',
      storesHint: 'Changes save as you go',
    },
    addresses: {
      loading: 'Loading addresses…',
      loadError: 'Addresses could not be loaded. Refresh the page to try again.',
      default: 'Default',
      makeDefault: 'Make default',
      edit: (address) => `Edit ${address}`,
      delete: (address) => `Delete ${address}`,
      add: 'Add address',
      editTitle: 'Edit address',
      newTitle: 'New ship-from address',
      street: 'Street',
      suite: 'Suite / unit (optional)',
      city: 'City',
      state: 'State',
      zip: 'ZIP',
      phone: 'Phone (optional)',
      defaultCheckbox: 'Default — used by stores without an address of their own',
      saveFailed: 'Save failed. Check the address and try again.',
      cancel: 'Cancel',
      saving: 'Saving…',
      save: 'Save address',
    },
    stores: {
      senderNameFor: (shop) => `Label sender name for ${shop}`,
      addressFor: (shop) => `Ship-from address for ${shop}`,
      defaultOption: (address) => `Default · ${address}`,
      noAddress: 'No address — labels blocked',
      replace: 'Replace',
      upload: 'Upload',
      remove: 'Remove',
      failed: 'Failed',
      saved: 'Saved',
      loading: 'Loading stores…',
      loadError: 'Stores could not be loaded. Refresh the page to try again.',
      searchPlaceholder: (count) => `Search ${count} stores`,
      searchLabel: 'Search stores',
      lastNotSaved: 'The last change was not saved. Try again.',
      colStore: 'Store',
      colShipFrom: 'Ship from',
      colSender: 'Sender name on label',
      colSlipLogo: 'Slip logo',
    },
  },
  tr: {
    page: {
      title: 'Gönderim Adresleri',
      description:
        'Etiketlerin gönderildiği adresler ve her mağazanın hangisini kullandığı. Etiketteki gönderici adı mağazanındır — boş bırakırsan mağaza adı basılır. Uygun adres yoksa etiket alınmaz.',
      addressesTitle: '1 · Adresler',
      addressesHint: 'Sokak, şehir, posta kodu — isim yok',
      storesTitle: '2 · Mağazalar',
      storesHint: 'Değişiklikler anında kaydedilir',
    },
    addresses: {
      loading: 'Adresler yükleniyor…',
      loadError: 'Adresler yüklenemedi. Tekrar denemek için sayfayı yenile.',
      default: 'Varsayılan',
      makeDefault: 'Varsayılan yap',
      edit: (address) => `${address} adresini düzenle`,
      delete: (address) => `${address} adresini sil`,
      add: 'Adres ekle',
      editTitle: 'Adresi düzenle',
      newTitle: 'Yeni gönderim adresi',
      street: 'Sokak / cadde',
      suite: 'Daire / birim (isteğe bağlı)',
      city: 'Şehir',
      state: 'Eyalet',
      zip: 'Posta kodu',
      phone: 'Telefon (isteğe bağlı)',
      defaultCheckbox: 'Varsayılan — kendi adresi olmayan mağazalar bunu kullanır',
      saveFailed: 'Kaydedilemedi. Adresi kontrol edip tekrar dene.',
      cancel: 'İptal',
      saving: 'Kaydediliyor…',
      save: 'Adresi kaydet',
    },
    stores: {
      senderNameFor: (shop) => `${shop} için etiketteki gönderici adı`,
      addressFor: (shop) => `${shop} için gönderim adresi`,
      defaultOption: (address) => `Varsayılan · ${address}`,
      noAddress: 'Adres yok — etiket alınamaz',
      replace: 'Değiştir',
      upload: 'Yükle',
      remove: 'Kaldır',
      failed: 'Başarısız',
      saved: 'Kaydedildi',
      loading: 'Mağazalar yükleniyor…',
      loadError: 'Mağazalar yüklenemedi. Tekrar denemek için sayfayı yenile.',
      searchPlaceholder: (count) => `${count} mağazada ara`,
      searchLabel: 'Mağaza ara',
      lastNotSaved: 'Son değişiklik kaydedilmedi. Tekrar dene.',
      colStore: 'Mağaza',
      colShipFrom: 'Gönderim adresi',
      colSender: 'Etiketteki gönderici adı',
      colSlipLogo: 'Fiş logosu',
    },
  },
}
